import React from 'react'

import { ICommentThread, ILoadedScript } from '#state'
import { fireAndForget } from '#util'

import * as domHelpers from './domHelpers'
import { EditedComment, ThreadContext } from './ThreadContext'
import { ThreadOperations } from './types'

export const OpenThreadProvider = ({
  script,
  thread,
  children,
}: {
  script: ILoadedScript
  thread: ICommentThread
  children: React.ReactNode
}) => {
  const { commentData } = script
  const [editedComment, setEditedComment] =
    React.useState<EditedComment | null>(null)

  const threadSelected = commentData.selectedThreadId === thread.id
  const [unsavedNewCommentText, setUnsavedNewCommentText] = React.useState('')

  React.useEffect(() => {
    if (!threadSelected && editedComment) {
      // oxlint-disable-next-line react/set-state-in-effect
      setEditedComment(null)
    }
  }, [editedComment, threadSelected])

  const operations: ThreadOperations = {
    changeThreadResolution: () =>
      fireAndForget(script.resolveCommentThread(thread.id)),
    addComment: (text: string) =>
      script.addCommentToThread({ threadId: thread.id, text }),
    editComment: (opts: { commentId: string; text: string }) => {
      fireAndForget(script.editComment(opts))
      setEditedComment(null)
    },
    deleteComment: (commentId: string) => {
      const comment = commentData.commentMap.get(commentId)
      if (comment) {
        fireAndForget(script.deleteComment(comment))
      }
    },
    selectThread: () => {
      commentData.selectThread(thread.id)
      // The node gets rerendered by prosemirror, setting a tiny delay
      // makes this scroll work most of the time
      setTimeout(() => {
        domHelpers.scrollToMark(thread.id, threadSelected)
      }, 30)
    },
  }

  return (
    <ThreadContext.Provider
      value={{
        thread,
        threadSelected,
        operations,
        unsavedNewCommentText,
        setUnsavedNewCommentText,
        editedComment,
        setEditedComment,
      }}
    >
      {children}
    </ThreadContext.Provider>
  )
}
